import { useEffect, useState } from "react";

import { CategoryTiles } from "@/components/home/category-tiles";
import { ContinueShoppingClient } from "@/components/home/continue-shopping";
import { HeroCarousel } from "@/components/home/hero-carousel";
import {
  BestSellers,
  LightningDeals,
  NewArrivals,
  PrimeExclusiveDeals,
  RecommendedForYou,
  SeasonalOffers,
  SponsoredProducts,
  TodaysDeals,
  TrendingProducts
} from "@/components/home/home-sections";
import { PrimePanel } from "@/components/home/prime-panel";
import { ProductShowcase } from "@/components/home/product-showcase";
import { RecentlyViewedHome } from "@/components/home/recently-viewed-home";
import { TrustBadgesBar } from "@/components/home/trust-badges-bar";
import { WebsiteExclusiveDiscounts } from "@/components/home/website-exclusive-discounts";
import { Skeleton } from "@/components/ui/skeleton";
import { EMPTY_HOME, getHome, type HomePayload } from "@/lib/queries";

/**
 * `app/page.tsx`.
 *
 * The original was a server component that awaited four queries for the
 * above-the-fold data and then streamed six more through `<Suspense>`. Now that
 * the data lives behind `GET /api/home`, the whole page is one fetch: the
 * skeleton below replaces the streaming fallback and is shown while it is in
 * flight.
 */
function SectionSkeleton() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs dark:border-white/10 dark:bg-slate-900">
      <Skeleton className="mb-2 h-6 w-48" />
      <Skeleton className="mb-4 h-4 w-72" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="aspect-square w-full rounded-lg" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function HomePage() {
  const [data, setData] = useState<HomePayload | null>(null);

  useEffect(() => {
    let active = true;
    void getHome().then((payload) => {
      if (active) setData(payload);
    });
    return () => {
      active = false;
    };
  }, []);

  // Below-the-fold sections render progressively as their data arrives, which is
  // what the streamed `<Suspense>` boundaries did on the server.
  const below = (children: React.ReactNode) => (data ? children : <SectionSkeleton />);

  if (!data) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-64 w-full rounded-xl" />
        <SectionSkeleton />
        <SectionSkeleton />
      </div>
    );
  }

  // Combine products for real discount detection
  const allDiscountCandidates = [
    ...(data.todaysDeals || []),
    ...(data.trending || []),
    ...(data.featured || [])
  ];

  return (
    <div className="pb-8 space-y-4 sm:space-y-6">
      <HeroCarousel slides={data.promotions} />

      {/* 4 Trust Badges from Official Sticker directly below Hero */}
      <TrustBadgesBar />

      {/* Prominent Website-Exclusive Discounts Banner & Products */}
      <WebsiteExclusiveDiscounts products={allDiscountCandidates} />

      <div className="relative z-10 space-y-5 sm:space-y-6">
        <div className="amazon-section">
          <CategoryTiles categories={data.categories} />
        </div>

        <TodaysDeals products={data.todaysDeals} />
        <LightningDeals products={data.todaysDeals} />

        {below(<TrendingProducts products={data.trending} />)}
        {below(<BestSellers products={data.bestSellers} />)}
        {below(<NewArrivals products={data.newArrivals} />)}
        {below(<RecommendedForYou products={data.recommended} />)}

        <RecentlyViewedHome />
        <ContinueShoppingClient />

        <ProductShowcase
          title="Featured Picks"
          subtitle="Prime badges, discounts, reviews, and quick cart actions."
          products={data.featured}
          href="/search"
        />

        <PrimePanel />

        {below(<PrimeExclusiveDeals products={data.primeDeals} />)}
        {below(<SponsoredProducts products={data.sponsored} />)}

        <SeasonalOffers />

        {below(
          <ProductShowcase
            title="Electronics and mobiles"
            subtitle="Launches, upgrades, audio gear, and essentials."
            products={data.electronics}
            href="/search?category=Electronics"
          />
        )}

        {below(
          <ProductShowcase
            title="Home, kitchen, and appliances"
            subtitle="Everyday products for modern Ethiopian homes."
            products={data.home}
            href="/search?category=Home%20%26%20Kitchen"
          />
        )}
      </div>
    </div>
  );
}

export { EMPTY_HOME };